/* W73 — EVERY PROGRESSION RULE AS A SENTENCE A COACH CAN READ (Fable's ruling 2026-09-25, W72 step 5).
 *
 * The TypeScript TWIN of `HybridTrainer/Services/AI/V6RuleFacts.swift` — `ruleFacts` (the parameters a
 * `progression_config` resolves to, with the rule's own defaults, and what it LACKS) and `ruleSentence` (the compact
 * sentence, the lacks said after a dash). It is held EQUAL to `__fixtures__/ruleSentenceCorpus.json`, which the Swift
 * EMITS (`KATA_EMIT_WEB_TOKENS=1`) over every distinct live config and one edge per rule and per missing key — the
 * W66 method, never a paraphrase: `ruleSentenceCorpus.test.ts` runs this file over the corpus's configs and compares
 * every field.
 *
 * Its readers: the builder's row tag (`builderHead.ruleTag`), the Workouts rows' "N rows without a rule" and the
 * Exercises foot line (`hasRule`). CLAUDE.md's rule: *"write every progression rule as a sentence an athlete could
 * follow, and the missing parameters announce themselves"* — `lacks` is that announcement, drawn in the accent.
 *
 * ⚠️ READ LIKE THE SWIFT READS JSON: a number is a number (a string "2.5" is NOT a step), an int is a WHOLE number, a
 * key present with `null` is present (it is not "omitted"), and an unknown rule word is reported, never "no rule".
 */

export type RuleLack = { key: string; words: string };

export type RuleKind =
  | { rule: "barbellLinear"; stepKg: number; holdWeeks: number; minRIR: number }
  | { rule: "weightedBwBiweekly"; stepKg: number; everyWeeks: number }
  | { rule: "repLadder"; switchReps: number | null; switchStepKg: number; startReps: number[] | null }
  | { rule: "holdRungBand"; requiredSessions: number; currentRung: string; nextRung: string | null }
  | { rule: "holdRungAssist"; assistStepKg: number }
  | { rule: "accessoryDouble"; low: number | null; high: number | null; stepKg: number | null }
  | { rule: "distanceCm"; stepCm: number; label: string; currentCm: number | null; repTarget: number | null; repStart: number | null }
  | { rule: "boxHeightCm"; steps: number[]; currentCm: number }
  | { rule: "marker" }
  | { rule: "unknown"; name: string };

export type RuleFacts = { kind: RuleKind; lacks: RuleLack[] };

type Raw = Record<string, unknown>;

function asObject(config: unknown): Raw | null {
  if (typeof config === "string") {
    try { return asObject(JSON.parse(config)); } catch { return null; }
  }
  if (config === null || typeof config !== "object" || Array.isArray(config)) return null;
  return config as Raw;
}
const has = (raw: Raw, key: string) => Object.prototype.hasOwnProperty.call(raw, key);
const num = (raw: Raw, key: string): number | null => (typeof raw[key] === "number" ? (raw[key] as number) : null);
const int = (raw: Raw, key: string): number | null => {
  const n = num(raw, key);
  return n !== null && Number.isInteger(n) ? n : null;
};
const text = (raw: Raw, key: string): string | null => (typeof raw[key] === "string" ? (raw[key] as string) : null);
const ints = (raw: Raw, key: string): number[] | null => {
  const a = raw[key];
  return Array.isArray(a) ? a.filter((x): x is number => typeof x === "number" && Number.isInteger(x)) : null;
};

/** `AssistLoad.kg`: a whole number bare, otherwise two decimals with trailing zeros trimmed. */
function kg(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}
/** `V6Progression.plain`: a whole number bare, otherwise one decimal. */
function plain(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

const RULES = new Set([
  "barbell_linear", "weighted_bw_biweekly", "rep_ladder", "hold_rung", "accessory_double", "distance_cm",
  "box_height_cm", "marker",
]);

/** The facts a stored config resolves to; NULL when it names no rule at all ("no progression rule"). */
export function ruleFacts(config: unknown): RuleFacts | null {
  const raw = asObject(config);
  if (raw === null) return null;
  const name = text(raw, "rule") ?? "";
  if (name === "") return null;
  const lacks: RuleLack[] = [];
  const need = (key: string, words: string) => { if (!has(raw, key)) lacks.push({ key, words }); };
  if (!RULES.has(name)) {
    return { kind: { rule: "unknown", name }, lacks: [{ key: "rule", words: "not a rule this build applies, nothing progresses" }] };
  }
  switch (name) {
    case "barbell_linear":
      need("step_kg", "step not set, 2.5 kg assumed");
      need("hold_weeks", "hold not set, 2 weeks assumed");
      need("min_rir", "reserve not set, 2 reps assumed");
      return { kind: { rule: "barbellLinear", stepKg: num(raw, "step_kg") ?? 2.5, holdWeeks: int(raw, "hold_weeks") ?? 2,
                       minRIR: num(raw, "min_rir") ?? 2 }, lacks };
    case "weighted_bw_biweekly":
      need("step_kg", "step not set, 2.5 kg assumed");
      need("every_weeks", "interval not set, 2 weeks assumed");
      return { kind: { rule: "weightedBwBiweekly", stepKg: num(raw, "step_kg") ?? 2.5, everyWeeks: int(raw, "every_weeks") ?? 2 }, lacks };
    case "rep_ladder": {
      const ceiling = int(raw, "switch_reps");
      if (ceiling === null) lacks.push({ key: "switch_reps", words: "no ceiling, reps climb for ever" });
      else {
        need("switch_step_kg", "load step not set, 2.5 kg assumed");
        need("start_reps", "no reset reps, back to the row's lowest");
      }
      return { kind: { rule: "repLadder", switchReps: ceiling, switchStepKg: num(raw, "switch_step_kg") ?? 2.5,
                       startReps: ints(raw, "start_reps") }, lacks };
    }
    case "hold_rung": {
      const ladder = Array.isArray(raw.band_ladder)
        ? (raw.band_ladder as unknown[]).filter((x): x is string => typeof x === "string") : null;
      if (ladder !== null && ladder.length >= 2) {
        need("thin_after_sessions", "sessions not set, 2 assumed");
        const index = Math.min(Math.max(0, int(raw, "band_index") ?? 0), ladder.length - 1);
        return { kind: { rule: "holdRungBand", requiredSessions: Math.max(1, int(raw, "thin_after_sessions") ?? 2),
                         currentRung: ladder[index]!, nextRung: index + 1 < ladder.length ? ladder[index + 1]! : null }, lacks };
      }
      need("assist_step_kg", "assistance step not set, 1.25 kg assumed");
      return { kind: { rule: "holdRungAssist", assistStepKg: num(raw, "assist_step_kg") ?? 1.25 }, lacks };
    }
    case "accessory_double":
      if (!has(raw, "rep_min") || !has(raw, "rep_max")) lacks.push({ key: "rep_min", words: "no rep range, the row's own range is used" });
      need("step_kg", "no load step, at the top it asks for a harder variation");
      return { kind: { rule: "accessoryDouble", low: int(raw, "rep_min"), high: int(raw, "rep_max"), stepKg: num(raw, "step_kg") }, lacks };
    case "distance_cm":
      need("step_cm", "step not set, 1 cm assumed");
      if (has(raw, "rep_target")) need("rep_start", "no reset reps, back to the row's lowest");
      return { kind: { rule: "distanceCm", stepCm: num(raw, "step_cm") ?? 1, label: text(raw, "distance_label") ?? "distance",
                       currentCm: num(raw, "current_cm"), repTarget: int(raw, "rep_target"), repStart: int(raw, "rep_start") }, lacks };
    case "box_height_cm": {
      need("steps", "no ladder of heights, nothing to step to");
      const steps = ints(raw, "steps") ?? [];
      return { kind: { rule: "boxHeightCm", steps, currentCm: int(raw, "current_cm") ?? steps[0] ?? 0 }, lacks };
    }
    default:
      return { kind: { rule: "marker" }, lacks: [] };
  }
}

/** The sentence without its lacks — the part drawn in ink. */
export function ruleSentenceBase(kind: RuleKind): string {
  switch (kind.rule) {
    case "barbellLinear":
      return `+${kg(kind.stepKg)} kg a week while every set keeps ${kg(kind.minRIR)} reps in reserve; `
        + `two missed jumps hold the load ${String(kind.holdWeeks)} weeks`;
    case "weightedBwBiweekly":
      return `+${kg(kind.stepKg)} kg after every ${String(kind.everyWeeks)} clean weeks`;
    case "repLadder": {
      if (kind.switchReps === null) return "+1 rep on one set each clean week";
      const back = kind.startReps === null ? "the row's lowest reps" : kind.startReps.join(",");
      if (kind.switchStepKg === 0) {
        return `+1 rep on one set each clean week to ${String(kind.switchReps)} on every set, then back to ${back} with no load added`;
      }
      return `+1 rep on one set each clean week to ${String(kind.switchReps)} on every set, then +${kg(kind.switchStepKg)} kg `
        + `and back to ${back}`;
    }
    case "holdRungBand": {
      const where = kind.nextRung === null ? `already on the last rung, ${kind.currentRung}` : `${kind.currentRung} → ${kind.nextRung}`;
      return `Seconds stay; ${String(kind.requiredSessions)} clean sessions thin the band a rung (${where})`;
    }
    case "holdRungAssist":
      return `Seconds stay; each clean week takes ${kg(kind.assistStepKg)} kg off the assistance`;
    case "accessoryDouble": {
      const range = kind.low !== null && kind.high !== null ? `${String(kind.low)}–${String(kind.high)}` : "the row's range";
      if (kind.stepKg === null) return `+1 rep a week inside ${range}`;
      return `+1 rep a week inside ${range}, then +${kg(kind.stepKg)} kg and back to ${kind.low === null ? "the bottom" : String(kind.low)}`;
    }
    case "distanceCm": {
      const move = `${kind.stepCm < 0 ? `−${plain(-kind.stepCm)} cm` : `+${plain(kind.stepCm)} cm`} ${kind.label}`;
      if (kind.repTarget === null) return `${move} each clean week`;
      const back = kind.repStart === null ? "" : ` and back to ${String(kind.repStart)} reps`;
      return `+1 rep to every set each clean week to ${String(kind.repTarget)}, then ${move}${back}`;
    }
    case "boxHeightCm": {
      const ladder = kind.steps.length === 0 ? "" : ` (${kind.steps.join(" → ")} cm)`;
      return `At ${String(kind.currentCm)} cm; a clean week you confirm earns the next height${ladder}`;
    }
    case "marker":
      return "Logged, never auto-progressed";
    case "unknown":
      return `“${kind.name}”`;
  }
}

/* 🔴 F15 STEP 3.5 — THE SHORT FORM, a coach's own words on ONE line (Fable, 2026-10-30 night: the row's "+1 rep on one set
 * each clean week to 7 on every set, then +2.5 kg and back to 5,5,5" wrapped two lines and read machine-made). WEB ONLY and
 * NOT in the Swift twin: the workout editor's row is its one reader; the long form above stays for the pop-up's second line
 * and for every surface the corpus pins. What the short form leaves out (the reserve, the hold, "clean", the reset reps) is
 * in the long form, one click away — never invented here. */
export function ruleShort(kind: RuleKind): string {
  switch (kind.rule) {
    case "barbellLinear":
      return `+${kg(kind.stepKg)} kg a week`;
    case "weightedBwBiweekly":
      return kind.everyWeeks === 1 ? `+${kg(kind.stepKg)} kg a week` : `+${kg(kind.stepKg)} kg every ${String(kind.everyWeeks)} weeks`;
    case "repLadder": {
      if (kind.switchReps === null) return "+1 rep a week";
      const top = `+1 rep a week to ${String(kind.switchReps)}`;
      if (kind.switchStepKg !== 0) return `${top}, then +${kg(kind.switchStepKg)} kg`;
      return kind.startReps === null || kind.startReps.length === 0 ? top : `${top}, then back to ${String(Math.min(...kind.startReps))}`;
    }
    case "holdRungBand":
      return kind.nextRung === null ? `Last band (${kind.currentRung})`
        : `Band ${kind.currentRung} → ${kind.nextRung} after ${String(kind.requiredSessions)} sessions`;
    case "holdRungAssist":
      return `−${kg(kind.assistStepKg)} kg assistance a week`;
    case "accessoryDouble": {
      const top = kind.high === null ? "+1 rep a week" : `+1 rep a week to ${String(kind.high)}`;
      return kind.stepKg === null ? top : `${top}, then +${kg(kind.stepKg)} kg`;
    }
    case "distanceCm": {
      const move = `${kind.stepCm < 0 ? `−${plain(-kind.stepCm)}` : `+${plain(kind.stepCm)}`} cm ${kind.label}`;
      return kind.repTarget === null ? `${move} a week` : `+1 rep a week to ${String(kind.repTarget)}, then ${move}`;
    }
    case "boxHeightCm":
      return `${String(kind.currentCm)} cm box, higher when confirmed`;
    case "marker":
      return "Logged, not progressed";
    case "unknown":
      return "Not progressing";
  }
}

/** The short form's one accent word for what the config never stated — the words themselves are the long form's. */
export function lacksShort(facts: RuleFacts): string | null {
  if (facts.lacks.length === 0) return null;
  return facts.kind.rule === "unknown" ? `“${facts.kind.name}” is not a rule` : "not fully set";
}

/** The whole sentence, lacks after a dash. */
export function ruleSentence(facts: RuleFacts): string {
  const base = ruleSentenceBase(facts.kind);
  return facts.lacks.length === 0 ? base : `${base} — ${facts.lacks.map((l) => l.words).join("; ")}`;
}

/** Does this config carry a rule? The ONE answer to "no rule" on every web surface: a config naming no rule is none,
 *  and an UNKNOWN rule word is not one either — nothing will progress the row. */
export function hasRule(config: unknown): boolean {
  const f = ruleFacts(config);
  return f !== null && f.kind.rule !== "unknown";
}
